const STORAGE_PREFIX = 'parties-pagination';
const LOCAL_PARTIES_STORAGE_PREFIX = 'local-parties-pagination';
const USERS_PAGINATION_PREFIX = 'users-pagination';
const DARS_PAGINATION_PREFIX = 'dars-pagination';

export interface PartiesPaginationState {
  pageIndex: number;
  pageSize: number;
  pageTokens: string[];
}

/**
 * Get storage key for a given participant (one key per participant).
 * Key: parties-pagination/participants/{participantName}/parties
 */
export const getPartiesPaginationStorageKey = (participantName: string): string => {
  return `${STORAGE_PREFIX}/participants/${participantName}/parties`;
};

const isValidState = (parsed: unknown): parsed is PartiesPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return (
    typeof s.pageIndex === 'number' &&
    s.pageIndex >= 0 &&
    typeof s.pageSize === 'number' &&
    s.pageSize > 0 &&
    Array.isArray(s.pageTokens) &&
    s.pageTokens.every((t) => typeof t === 'string') &&
    s.pageTokens.length > s.pageIndex
  );
};

/**
 * Load parties pagination state from localStorage
 */
export const loadPartiesPaginationState = (participantName: string): PartiesPaginationState | null => {
  try {
    const key = getPartiesPaginationStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load parties pagination state from localStorage:', error);
    return null;
  }
};

/**
 * Save parties pagination state to localStorage.
 * Only tokens needed for the current page are stored.
 */
export const savePartiesPaginationState = (participantName: string, state: PartiesPaginationState): void => {
  try {
    const key = getPartiesPaginationStorageKey(participantName);
    const toStore = {
      ...state,
      pageTokens: state.pageTokens.slice(0, state.pageIndex + 1),
    };
    localStorage.setItem(key, JSON.stringify(toStore));
  } catch (error) {
    console.warn('Failed to save parties pagination state to localStorage:', error);
  }
};

/**
 * Clear parties pagination state for a specific participant
 */
export const clearPartiesPaginationState = (participantName: string): void => {
  try {
    const key = getPartiesPaginationStorageKey(participantName);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear parties pagination state from localStorage:', error);
  }
};

/**
 * Clear all parties pagination states (used during logout)
 */
export const clearAllPartiesPaginationStates = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const prefixes = [STORAGE_PREFIX, LOCAL_PARTIES_STORAGE_PREFIX, USERS_PAGINATION_PREFIX, DARS_PAGINATION_PREFIX];
    keys.forEach((key) => {
      if (prefixes.some((p) => key.startsWith(p))) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear all parties pagination states from localStorage:', error);
  }
};

// -------------------- Local parties (client-side pagination) -------------------- //

export interface LocalPartiesPaginationState {
  pageIndex: number;
  pageSize: number;
}

/** One key per participant: local-parties-pagination/participants/{participantName}/local-parties */
export const getLocalPartiesPaginationStorageKey = (participantName: string): string => {
  return `${LOCAL_PARTIES_STORAGE_PREFIX}/participants/${participantName}/local-parties`;
};

const isValidLocalPartiesState = (parsed: unknown): parsed is LocalPartiesPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return typeof s.pageIndex === 'number' && s.pageIndex >= 0 && typeof s.pageSize === 'number' && s.pageSize > 0;
};

export const loadLocalPartiesPaginationState = (participantName: string): LocalPartiesPaginationState | null => {
  try {
    const key = getLocalPartiesPaginationStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidLocalPartiesState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load local parties pagination state from localStorage:', error);
    return null;
  }
};

export const saveLocalPartiesPaginationState = (participantName: string, state: LocalPartiesPaginationState): void => {
  try {
    const key = getLocalPartiesPaginationStorageKey(participantName);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save local parties pagination state to localStorage:', error);
  }
};

// -------------------- Local parties list (persisted for refresh) -------------------- //

/** One key per participant: local-parties-pagination/participants/{participantName}/local-parties-list */
const getLocalPartiesListStorageKey = (participantName: string): string => {
  return `${LOCAL_PARTIES_STORAGE_PREFIX}/participants/${participantName}/local-parties-list`;
};

const isValidLocalPartiesList = (parsed: unknown): parsed is string[] => {
  return Array.isArray(parsed) && parsed.every((item) => typeof item === 'string');
};

export const loadLocalPartiesList = (participantName: string): string[] | null => {
  try {
    const key = getLocalPartiesListStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidLocalPartiesList(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load local parties list from localStorage:', error);
    return null;
  }
};

export const saveLocalPartiesList = (participantName: string, partyIds: string[]): void => {
  try {
    const key = getLocalPartiesListStorageKey(participantName);
    localStorage.setItem(key, JSON.stringify(partyIds));
  } catch (error) {
    console.warn('Failed to save local parties list to localStorage:', error);
  }
};

// -------------------- Users tab (client-side pagination) -------------------- //

export interface UsersPaginationState {
  pageIndex: number;
  pageSize: number;
}

/** One key per participant: users-pagination/participants/{participantName}/users */
export const getUsersPaginationStorageKey = (participantName: string): string => {
  return `${USERS_PAGINATION_PREFIX}/participants/${participantName}/users`;
};

const isValidUsersPaginationState = (parsed: unknown): parsed is UsersPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return typeof s.pageIndex === 'number' && s.pageIndex >= 0 && typeof s.pageSize === 'number' && s.pageSize > 0;
};

export const loadUsersPaginationState = (participantName: string): UsersPaginationState | null => {
  try {
    const key = getUsersPaginationStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidUsersPaginationState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load users pagination state from localStorage:', error);
    return null;
  }
};

export const saveUsersPaginationState = (participantName: string, state: UsersPaginationState): void => {
  try {
    const key = getUsersPaginationStorageKey(participantName);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save users pagination state to localStorage:', error);
  }
};

// -------------------- DAR files tab (client-side pagination) -------------------- //

export interface DarsPaginationState {
  pageIndex: number;
  pageSize: number;
}

/** One key per participant: dars-pagination/participants/{participantName}/dars */
export const getDarsPaginationStorageKey = (participantName: string): string => {
  return `${DARS_PAGINATION_PREFIX}/participants/${participantName}/dars`;
};

const isValidDarsPaginationState = (parsed: unknown): parsed is DarsPaginationState => {
  if (!parsed || typeof parsed !== 'object') return false;
  const s = parsed as Record<string, unknown>;
  return typeof s.pageIndex === 'number' && s.pageIndex >= 0 && typeof s.pageSize === 'number' && s.pageSize > 0;
};

export const loadDarsPaginationState = (participantName: string): DarsPaginationState | null => {
  try {
    const key = getDarsPaginationStorageKey(participantName);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as unknown;
    return isValidDarsPaginationState(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to load dars pagination state from localStorage:', error);
    return null;
  }
};

export const saveDarsPaginationState = (participantName: string, state: DarsPaginationState): void => {
  try {
    const key = getDarsPaginationStorageKey(participantName);
    localStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save dars pagination state to localStorage:', error);
  }
};
